import React from 'react';
import { useQuery } from 'react-query';

import { useForm, useSubmit } from '@reckon-web/forms';

import { WidgetContainer } from '../../generic/wigetContainer';
import { ErrorResponseType } from '../../useErrorResponses';
import { checkExistingEmail } from '../../../lib/api';

import { SingupFormSchema } from './formSchema';
import { SignUpForm } from './form';
import { AmplifySignUpRequest } from './types';

type countries = 'AU' | 'NZ' | 'GB';

const countryCodes: Record<countries, string> = {
  AU: '+61',
  NZ: '+64',
  GB: '+44',
};

export type SignUpProps = {
  handleSignUp: (formData: AmplifySignUpRequest) => Promise<any>;
  submitting?: boolean;
  onLoginClick: () => void;
  error?: ErrorResponseType;
  email?: string;
  inviteId?: string;
  SingleSignUpActions?: {
    microsoft?: { onClick: () => void };
    google?: { onClick: () => void };
    apple?: { onClick: () => void };
  };
};

export const SignUp: React.FC<SignUpProps> = ({
  submitting,
  handleSignUp,
  onLoginClick,
  error,
  email,
  inviteId,
  SingleSignUpActions,
}) => {
  const form = useForm(SingupFormSchema, {
    country: 'AU',
    ...(inviteId && email ? { email } : {}),
  });
  const checkEmailExists = useQuery(
    ['emailRegistered', form.fields.email.value, form.fields.email.validity],
    ({ queryKey }) => checkExistingEmail(queryKey[1], queryKey[2])
  );

  const formatPhoneNumber = (country: countries, phone: string) =>
    countryCodes[country] + phone.substring(1);

  // place holder until we implement SSO signup
  const todo = () => {};

  const handleSubmit = useSubmit(form, (data) => {
    handleSignUp({
      username: data.email,
      password: data.password,
      attributes: {
        email: data.email,
        phone_number: formatPhoneNumber(
          data.country as countries,
          data.phonenumber
        ),
        locale: data.country,
        family_name: data.lastname,
        given_name: data.firstname,
      },
      ...(inviteId && email ? { clientMetadata: { inviteId } } : {}),
    });
  });

  return (
    <WidgetContainer>
      <SignUpForm
        form={form}
        submitting={submitting}
        alreadyRegistered={
          checkEmailExists.isSuccess &&
          checkEmailExists.data.result === 'success'
        }
        email={email || ''}
        inviteId={inviteId || ''}
        onSubmit={handleSubmit}
        onLoginClick={onLoginClick}
        onSignInWithAppleClick={SingleSignUpActions?.apple ? todo : undefined}
        onSignInWithMicrosoftClick={
          SingleSignUpActions?.microsoft ? todo : undefined
        }
        onSigninWithGoogleClick={SingleSignUpActions?.google ? todo : undefined}
        error={error}
      />
    </WidgetContainer>
  );
};
